/**
 * @brisa/ui · Dialog
 *
 * Implementação com focus trap básico e close via Escape.
 * API compatível com Radix (defaultOpen, title, description, children).
 */
import * as React from 'react';

export interface DialogProps {
  defaultOpen?: boolean;
  title: string;
  description?: string;
  children?: React.ReactNode;
}

export function Dialog({ defaultOpen = false, title, description, children }: DialogProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!open) return undefined;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      // Focus trap: Tab cicla entre focáveis do dialog (evita escapar para fora).
      if (e.key === 'Tab' && contentRef.current) {
        const focusables = contentRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (!e.shiftKey && active === last) {
          e.preventDefault();
          first?.focus();
        } else if (e.shiftKey && active === first) {
          e.preventDefault();
          last?.focus();
        } else if (active && !contentRef.current.contains(active)) {
          // Foco escapou por algum motivo — trazer de volta
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);

    // Focus trap: move focus para close button assim que o dialog abre.
    closeBtnRef.current?.focus();

    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <>
      <span
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
      >
        {children}
      </span>
      {open ? (
        <div className="brisa-dialog-overlay" onClick={() => setOpen(false)}>
          <div
            ref={contentRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="brisa-dialog-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="brisa-dialog-title brisa-display">{title}</h2>
            {description ? <p className="brisa-dialog-desc">{description}</p> : null}
            <button
              ref={closeBtnRef}
              type="button"
              className="brisa-dialog-close"
              aria-label="Fechar"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

Dialog.displayName = 'Dialog';
